/* eslint-disable @typescript-eslint/no-shadow */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { gql, useQuery, useMutation, useSubscription } from "@apollo/client";
import { Box, CircularProgress, Typography } from "@mui/material";
import expressConfig from "src/config/express.json";
import WidgetPreview from "../settings/rightPanel/chatbot/shared/WidgetPreview";
import {
  ChatbotConfig,
  defaultChatbotConfig,
} from "../settings/rightPanel/chatbot/shared/types";

const GET_DEPLOYMENT_WIDGET = gql`
  query GetDeploymentWidget($deploymentId: String!) {
    getDeployment(deploymentId: $deploymentId) {
      deploymentId
      bot {
        botId
        name
        primaryColor
        secondaryColor
        position
        avatarUrl
        launcherImageUrl
        darkMode
        welcomeMessage
        placeholderText
        autoOpen
        autoOpenDelay
        typingAnimation
        botTakeoverName
        agentTakeoverName
        backgroundColor
        borderRadius
        soundEnabled
        links
        enabled
      }
      widgetConfig
    }
  }
`;

const SEND_CHAT_MESSAGE = gql`
  mutation SendChatbotMessage($input: SendChatbotMessageInput!) {
    sendChatbotMessage(input: $input) {
      success
      error
      message {
        messageId
        conversationId
        content
        role
        fileUrl
        fileType
        createdAt
      }
      messages {
        messageId
        conversationId
        content
        role
        fileUrl
        fileType
        createdAt
      }
    }
  }
`;

const GET_OR_CREATE_CONVERSATION = gql`
  query GetOrCreateChatbotConversation(
    $deploymentId: String!
    $sessionId: String
    $customerId: String
    $customerName: String
  ) {
    getOrCreateChatbotConversation(
      deploymentId: $deploymentId
      sessionId: $sessionId
      customerId: $customerId
      customerName: $customerName
    ) {
      conversationId
      status
      thumbsUpDown
      feedbackMessage
      closedByCustomer
      messages {
        messageId
        content
        role
        fileUrl
        fileType
        createdAt
      }
    }
  }
`;

const CHATBOT_MESSAGE_SUBSCRIPTION = gql`
  subscription OnChatbotMessageAdded($conversationId: String!) {
    chatbotMessageAdded(conversationId: $conversationId) {
      messageId
      content
      role
      fileUrl
      fileType
      createdAt
    }
  }
`;

const CHATBOT_TYPING_SUBSCRIPTION = gql`
  subscription OnChatbotTypingStatusChanged($conversationId: String!) {
    chatbotTypingStatusChanged(conversationId: $conversationId)
  }
`;

const CLOSE_CHATBOT_BY_CUSTOMER = gql`
  mutation CloseChatbotByCustomer($conversationId: String!) {
    closeChatbotConversationByCustomer(conversationId: $conversationId) {
      success
      error
    }
  }
`;

const SUBMIT_CHATBOT_FEEDBACK = gql`
  mutation SubmitChatbotFeedback(
    $conversationId: String!
    $thumbsUpDown: String!
    $feedbackMessage: String!
  ) {
    submitChatbotFeedback(
      conversationId: $conversationId
      thumbsUpDown: $thumbsUpDown
      feedbackMessage: $feedbackMessage
    ) {
      success
      error
    }
  }
`;

// Audio transcription is done via REST (POST /audio/transcribe)
// to avoid the graphql-upload ↔ express-fileupload multipart conflict.

interface Message {
  id: string;
  text: string;
  sender: "bot" | "user";
  timestamp: Date;
  fileUrl?: string;
  fileType?: string;
  isSystem?: boolean;
  isSending?: boolean;
}

const WidgetChat: React.FC = () => {
  const { deploymentId } = useParams<{ deploymentId: string }>();
  const [messages, setMessages] = useState<Message[]>([]);
  const [isBotThinking, setIsBotThinking] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isAgentTyping, setIsAgentTyping] = useState(false);
  const [botError, setBotError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [conversationStatus, setConversationStatus] = useState<
    "active" | "handed_off" | "completed"
  >("active");

  const [isClosedByCustomer, setIsClosedByCustomer] = useState(false);
  const [initialThumbs, setInitialThumbs] = useState<string | null>(null);
  const [initialFeedback, setInitialFeedback] = useState<string | null>(null);

  const { data, loading, error } = useQuery(GET_DEPLOYMENT_WIDGET, {
    variables: { deploymentId },
    skip: !deploymentId,
  });

  const deployment = data?.getDeployment;
  const bot = deployment?.bot;

  // Construct full config using defaults as base
  const config: ChatbotConfig = {
    ...defaultChatbotConfig,
    id: bot?.botId || "",
    entityId: deployment?.deploymentId || "",
    entityType: "widget",
    vectorNamespace: deployment?.vectorNamespace || "",
    enabled: bot?.enabled ?? true,
    appearance: {
      ...defaultChatbotConfig.appearance,
      primaryColor:
        bot?.primaryColor || defaultChatbotConfig.appearance.primaryColor,
      secondaryColor:
        bot?.secondaryColor || defaultChatbotConfig.appearance.secondaryColor,
      backgroundColor:
        bot?.backgroundColor || defaultChatbotConfig.appearance.backgroundColor,
      borderRadius:
        bot?.borderRadius || defaultChatbotConfig.appearance.borderRadius,
      position:
        (bot?.position as any) || defaultChatbotConfig.appearance.position,
      avatarUrl: bot?.avatarUrl,
      launcherImageUrl: bot?.launcherImageUrl,
      darkMode: !!bot?.darkMode,
    },
    behavior: {
      ...defaultChatbotConfig.behavior,
      welcomeMessage:
        bot?.welcomeMessage || defaultChatbotConfig.behavior.welcomeMessage,
      placeholderText:
        bot?.placeholderText || defaultChatbotConfig.behavior.placeholderText,
      autoOpen: !!bot?.autoOpen,
      autoOpenDelay:
        bot?.autoOpenDelay || defaultChatbotConfig.behavior.autoOpenDelay,
      typingAnimation:
        bot?.typingAnimation ?? defaultChatbotConfig.behavior.typingAnimation,
      soundEnabled: !!bot?.soundEnabled,
      botTakeoverName:
        bot?.botTakeoverName || defaultChatbotConfig.behavior.botTakeoverName,
      agentTakeoverName:
        bot?.agentTakeoverName ||
        defaultChatbotConfig.behavior.agentTakeoverName,
    },
    links: bot?.links || [],
  };

  const [sessionId, setSessionId] = useState<string>(
    sessionStorage.getItem("chatbot_session_id") ||
      (() => {
        const id = Math.random().toString(36).substring(2, 15);
        sessionStorage.setItem("chatbot_session_id", id);
        return id;
      })(),
  );

  const [isResetting, setIsResetting] = useState(false);

  const { data: convResp, loading: convLoading } = useQuery(
    GET_OR_CREATE_CONVERSATION,
    {
      variables: {
        deploymentId,
        sessionId,
        customerName: "Anonymous",
      },
      skip: !deploymentId,
      onError: () => {
        if (isResetting) setIsResetting(false);
      },
    },
  );

  // Handle conversation data updates (including reset)
  useEffect(() => {
    if (!convLoading && convResp) {
      // If we were in a resetting state, we clear it now that the query (new session) has returned
      if (isResetting) {
        setMessages([]);
        setConversationId(null);
        setConversationStatus("active");
        setIsClosedByCustomer(false);
        setInitialThumbs(null);
        setInitialFeedback(null);
        setBotError(null);
        setIsResetting(false);
      }

      const conversation = convResp.getOrCreateChatbotConversation;
      if (conversation) {
        setConversationId(conversation.conversationId);
        setConversationStatus(conversation.status || "active");
        setIsClosedByCustomer(!!conversation.closedByCustomer);
        setInitialThumbs(conversation.thumbsUpDown);
        setInitialFeedback(conversation.feedbackMessage);

        // Load existing messages
        if (conversation.messages) {
          const historicalMessages: Message[] = conversation.messages
            .filter((m: any) => m.role !== "system")
            .map((m: any) => {
              const rawText = m.content || "";
              const isSystem =
                m.role === "chatbot_system" || rawText.startsWith("__SYSTEM__");
              return {
                id: m.messageId,
                text: rawText.replace("__SYSTEM__", ""),
                sender: m.role === "user" ? "user" : "bot",
                timestamp: new Date(m.createdAt || Date.now()),
                fileUrl: m.fileUrl,
                fileType: m.fileType,
                isSystem,
              };
            });

          setMessages((prev) => {
            // Keep welcome message if it exists
            const welcome = prev.find((m) => m.id === "welcome");
            if (welcome) {
              return [welcome, ...historicalMessages];
            }
            return historicalMessages;
          });
        }
      } else {
        // Conversation doesn't exist yet (brand new session)
        setConversationId(null);
        setConversationStatus("active");
        setIsClosedByCustomer(false);
      }
    }
  }, [convResp, convLoading, isResetting]);

  // Subscription for new messages
  useSubscription(CHATBOT_MESSAGE_SUBSCRIPTION, {
    variables: { conversationId },
    skip: !conversationId,
    onData: ({ data }) => {
      const newMessage = data.data?.chatbotMessageAdded;
      if (newMessage) {
        // 0. Ignore system logs in the widget
        if (newMessage.role === "system") return;

        setMessages((prev) => {
          // 1. Deduplicate by exact ID
          if (prev.find((m) => m.id === newMessage.messageId)) return prev;

          // 2. Deduplicate user messages by content and approximate time
          if (newMessage.role === "user") {
            const msgDate = new Date(newMessage.createdAt).getTime();
            const duplicate = prev.find(
              (m) =>
                m.sender === "user" &&
                m.text === newMessage.content &&
                Math.abs(m.timestamp.getTime() - msgDate) < 5000,
            );
            if (duplicate) {
              return prev.map((m) =>
                m.id === duplicate.id
                  ? { ...m, id: newMessage.messageId, isSending: false }
                  : m,
              );
            }
          }

          const rawText = newMessage.content || "";
          const isSystem =
            newMessage.role === "chatbot_system" ||
            rawText.startsWith("__SYSTEM__");

          const formattedMsg: Message = {
            id: newMessage.messageId,
            text: rawText.replace("__SYSTEM__", ""),
            sender: newMessage.role === "user" ? "user" : "bot",
            timestamp: new Date(newMessage.createdAt || Date.now()),
            fileUrl: newMessage.fileUrl,
            fileType: newMessage.fileType,
            isSystem,
          };

          return [...prev, formattedMsg];
        });

        // Play sound if enabled and message is from bot/agent
        if (newMessage.role === "assistant" && config.behavior.soundEnabled) {
          try {
            const audio = new Audio(
              "https://assets.mixkit.co/active_storage/sfx/2358/2358-preview.mp3",
            );
            audio
              .play()
              .catch(() => console.log("Sound play blocked by browser policy"));
          } catch (e) {
            console.error("Error playing sound:", e);
          }
        }

        // If message is from bot/agent, stop thinking indicator
        if (newMessage.role === "assistant") {
          setIsBotThinking(false);
        }
      }
    },
  });

  // Subscription for typing status
  useSubscription(CHATBOT_TYPING_SUBSCRIPTION, {
    variables: { conversationId },
    skip: !conversationId,
    onData: ({ data }) => {
      const typingStatus = data.data?.chatbotTypingStatusChanged;
      if (typeof typingStatus === "boolean") {
        setIsAgentTyping(typingStatus);
      }
    },
  });

  const [sendMessageMutation] = useMutation(SEND_CHAT_MESSAGE);
  const [closeByCustomerMutation] = useMutation(CLOSE_CHATBOT_BY_CUSTOMER);
  const [submitFeedbackMutation] = useMutation(SUBMIT_CHATBOT_FEEDBACK);

  const handleTranscribe = async (
    audio: Blob,
  ): Promise<{ success: boolean; text?: string; error?: string }> => {
    try {
      const env = (process.env.NODE_ENV ||
        "development") as keyof typeof expressConfig;
      const cfg = expressConfig[env] as {
        graphql_domain: string;
        port: number;
      };
      const baseUrl = `${cfg.graphql_domain}:${cfg.port}`;

      const formData = new FormData();
      formData.append("audio", audio, "audio.webm");

      const response = await fetch(`${baseUrl}/audio/transcribe`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`Transcription request failed (${response.status})`);
      }

      const result = await response.json();
      if (result.text) {
        return { success: true, text: result.text };
      }
      return {
        success: false,
        error: result.error || "No transcript returned",
      };
    } catch (err: any) {
      console.error("Error transcribing audio:", err);
      return { success: false, error: err.message };
    }
  };

  const handleCloseByCustomer = async () => {
    if (!conversationId) return;
    try {
      await closeByCustomerMutation({
        variables: { conversationId },
      });
      setConversationStatus("completed");
      setIsClosedByCustomer(true);
    } catch (err) {
      console.error("Error closing chat:", err);
    }
  };

  const handleSubmitFeedback = async (rating: "up" | "down", ease: string) => {
    if (!conversationId) return;
    try {
      await submitFeedbackMutation({
        variables: {
          conversationId,
          thumbsUpDown: rating,
          feedbackMessage: ease,
        },
      });
      setInitialThumbs(rating);
      setInitialFeedback(ease);
    } catch (err) {
      console.error("Error submitting feedback:", err);
    }
  };

  const handleResetChat = () => {
    setIsResetting(true);
    const newSessionId = Math.random().toString(36).substring(2, 15);
    sessionStorage.setItem("chatbot_session_id", newSessionId);

    // Update state to trigger re-render and query
    setSessionId(newSessionId);
  };

  useEffect(() => {
    if (bot && !loading && !convLoading) {
      const welcomeText = bot.welcomeMessage || "Hi! How can I help you today?";

      // Ensure welcome message is present ONLY if no other messages exist
      setMessages((prev) => {
        // Determine if we already have a welcome message
        const hasWelcome = prev.some((m) => m.id === "welcome");
        const hasHistory = prev.some((m) => m.id !== "welcome");

        if (hasWelcome) {
          // Just update the text if it changed in config
          return prev.map((m) =>
            m.id === "welcome" && m.text !== welcomeText
              ? { ...m, text: welcomeText }
              : m,
          );
        }

        // If we have history, don't prepend a welcome message (it should be in history or skipped)
        if (hasHistory) return prev;

        // Only add welcome message if the chat is completely empty
        return [
          {
            id: "welcome",
            text: welcomeText,
            sender: "bot",
            timestamp: new Date(),
          },
        ];
      });

      console.log("Kuikwit Chatbot: Sending config to parent:", {
        launcherImageUrl: bot.launcherImageUrl,
        primaryColor: bot.primaryColor,
        position: bot.position,
      });
      window.parent.postMessage(
        {
          type: "KUIKWIT_CHATBOT_CONFIG",
          config: {
            launcherImageUrl: bot.launcherImageUrl,
            primaryColor: bot.primaryColor,
            backgroundColor: bot.backgroundColor,
            borderRadius: bot.borderRadius,
            position: bot.position,
            autoOpen: bot.autoOpen,
            autoOpenDelay: bot.autoOpenDelay,
            enabled: bot.enabled,
          },
        },
        "*",
      );
    }
  }, [data, loading, convLoading, bot?.welcomeMessage]);

  const handleSendMessage = async (text: string, file?: File) => {
    if (!deploymentId) return;

    setBotError(null); // Clear previous errors

    // Add user message locally for instant feedback
    const localId = Date.now().toString();
    const userMsg: Message = {
      id: localId,
      text,
      sender: "user",
      timestamp: new Date(),
      fileUrl: file ? URL.createObjectURL(file) : undefined,
      fileType: file ? file.type : undefined,
      isSending: true,
    };

    setMessages((prev) => [...prev, userMsg]);
    setIsSending(true);
    setIsBotThinking(true); // Start thinking immediately so subscription can flip it back

    try {
      const { data: result } = await sendMessageMutation({
        variables: {
          input: {
            deploymentId,
            message: text,
            sessionId,
            file,
          },
        },
      });

      if (result?.sendChatbotMessage?.success) {
        setIsSending(false);

        const botReplies =
          result.sendChatbotMessage.messages ||
          (result.sendChatbotMessage.message
            ? [result.sendChatbotMessage.message]
            : []);

        if (botReplies.length > 0) {
          // 1. Set conversation ID if not already set (critical for first message)
          if (!conversationId) {
            setConversationId(botReplies[0].conversationId);
          }

          // 2. Add all bot responses to the messages list
          // This ensures follow-up messages aren't missed if the subscription isn't ready
          const formattedReplies: Message[] = botReplies.map((reply: any) => ({
            id: reply.messageId,
            text: (reply.content || "").replace("__SYSTEM__", ""),
            sender: "bot",
            timestamp: new Date(reply.createdAt || Date.now()),
            fileUrl: reply.fileUrl,
            fileType: reply.fileType,
            isSystem:
              reply.role === "chatbot_system" ||
              (reply.content || "").startsWith("__SYSTEM__"),
          }));

          setMessages((prev) => {
            const existingIds = new Set(prev.map((m) => m.id));
            const newReplies = formattedReplies.filter(
              (r) => !existingIds.has(r.id),
            );
            if (newReplies.length === 0) return prev;
            return [...prev, ...newReplies];
          });

          // 3. Stop thinking indicator
          setIsBotThinking(false);
        } else {
          // If no message returned (e.g. handoff or suppressed response)
          setIsBotThinking(false);
        }
      } else {
        // Handle success: false but with an error message
        const errorMsg =
          result?.sendChatbotMessage?.error ||
          "Failed to get a response from the AI.";
        setBotError(errorMsg);
        console.error("Chatbot response error:", errorMsg);
        setIsSending(false);
        setIsBotThinking(false);
      }
    } catch (err: any) {
      const errorMsg = err.message || "An unexpected error occurred.";
      setBotError(errorMsg);
      console.error("Mutation error:", err);
      setIsSending(false);
      setIsBotThinking(false);
    }
  };

  if (loading || (convLoading && messages.length === 0 && !isResetting)) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !deployment) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography color="error">
          {error ? error.message : "Widget not found or inactive."}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100vh",
        width: "100vw",
        overflow: "hidden",
        margin: 0,
        padding: 0,
        position: "fixed",
        top: 0,
        left: 0,
        bgcolor:
          config.appearance.backgroundColor ||
          (config.appearance.darkMode ? "#121212" : "#fff"),
      }}
    >
      <WidgetPreview
        config={config}
        isStandalone
        externalMessages={messages}
        onSendMessage={handleSendMessage}
        isTyping={
          (isBotThinking || isAgentTyping) &&
          config.behavior.typingAnimation &&
          !isSending
        }
        isSending={isSending}
        botError={botError}
        status={conversationStatus}
        onCloseChat={handleCloseByCustomer}
        onSubmitFeedback={handleSubmitFeedback}
        conversationId={conversationId}
        closedByCustomer={isClosedByCustomer}
        initialThumbs={initialThumbs}
        initialFeedback={initialFeedback}
        onTranscribe={handleTranscribe}
        onResetChat={handleResetChat}
        isResetLoading={isResetting}
      />
    </Box>
  );
};

export default WidgetChat;
